import {
  ExternalRouteRef,
  RouteRef,
  SubRouteRef,
  createExternalRouteRef,
} from '@backstage/core-plugin-api';

import {
  createRootRouteRefsPair,
  createSubRouteRefsPair,
} from '@spotify/confidence-core-react';

export enum RoutesIds {
  flags = 'flags',
  resolverTest = 'resolverTest',
  flag = 'flag',
  flagsTools = 'flagsTools',
}

enum ExternalRoutesIds {
  workflow = 'workflow',
  workflowInstance = 'workflowInstance',
  clients = 'clients',
  clientSchema = 'clientSchema',
  client = 'client',
  materializedSegments = 'materializedSegments',
  materializedSegment = 'materializedSegment',
  materializedSegmentLoadJob = 'materializedSegmentLoadJob',
  segments = 'segments',
  segment = 'segment',
  labels = 'labels',
}

// root route
export const [flagsRouteImplRef, flagsRouteRef] = createRootRouteRefsPair({
  id: RoutesIds.flags,
});

export const rootRouteRef = flagsRouteImplRef;

// sub-routes within the plugin
export const [resolverTesterRouteImplRef, resolverTesterRouteRef] =
  createSubRouteRefsPair({
    id: RoutesIds.resolverTest,
    parent: rootRouteRef,
    path: '/resolver-test',
  });

export const [flagRouteImplRef, flagRouteRef] = createSubRouteRefsPair({
  id: RoutesIds.flag,
  parent: rootRouteRef,
  path: '/:id',
});

export const [flagsToolsRouteImplRef, flagsToolsRouteRef] =
  createSubRouteRefsPair({
    id: RoutesIds.flagsTools,
    parent: rootRouteRef,
    path: '/tools',
  });

// external routes to other plugins
export const workflowRouteRef = createExternalRouteRef({
  id: ExternalRoutesIds.workflow,
  params: ['workflow'],
  optional: true,
});

export const workflowInstanceRouteRef = createExternalRouteRef({
  id: ExternalRoutesIds.workflowInstance,
  params: ['workflow', 'id'],
  optional: true,
});

export const clientsRouteRef = createExternalRouteRef({
  id: ExternalRoutesIds.clients,
  optional: true,
});

export const clientSchemaRouteRef = createExternalRouteRef({
  id: ExternalRoutesIds.clientSchema,
  optional: true,
});

export const clientRouteRef = createExternalRouteRef({
  id: ExternalRoutesIds.client,
  params: ['id'],
  optional: true,
});

// Segments routes as Segments and materialized segments components still lives in
// flags plugin. Once we migrate these components to the new modules,
// we can remove these routes.
export const materializedSegmentsRouteRef = createExternalRouteRef({
  id: ExternalRoutesIds.materializedSegments,
});

export const materializedSegmentRouteRef = createExternalRouteRef({
  id: ExternalRoutesIds.materializedSegment,
  params: ['id'],
});

export const materializedSegmentLoadJobRouteRef = createExternalRouteRef({
  id: ExternalRoutesIds.materializedSegmentLoadJob,
  params: ['id'],
});

export const segmentsRouteRef = createExternalRouteRef({
  id: ExternalRoutesIds.segments,
});

export const segmentRouteRef = createExternalRouteRef({
  id: ExternalRoutesIds.segment,
  params: ['id'],
});

export const labelsRouteRef = createExternalRouteRef({
  id: ExternalRoutesIds.labels,
  optional: true,
});

// default routes/routes to be used by other plugins
export const Routes = {
  flags: flagsRouteImplRef,
  resolverTest: resolverTesterRouteImplRef,
  flag: flagRouteImplRef,
  flagsTools: flagsToolsRouteImplRef,
} as Record<
  'flags' | 'resolverTest' | 'flag' | 'flagsTools',
  SubRouteRef | RouteRef
>;

export const ExternalRoutes = {
  // sub-routes within the plugin
  flags: flagsRouteRef,
  resolverTest: resolverTesterRouteRef,
  flag: flagRouteRef,
  flagsTools: flagsToolsRouteRef,

  // external routes to other plugins
  workflow: workflowRouteRef,
  workflowInstance: workflowInstanceRouteRef,
  clients: clientsRouteRef,
  clientSchema: clientSchemaRouteRef,
  client: clientRouteRef,
  materializedSegments: materializedSegmentsRouteRef,
  materializedSegment: materializedSegmentRouteRef,
  materializedSegmentLoadJob: materializedSegmentLoadJobRouteRef,
  segments: segmentsRouteRef,
  segment: segmentRouteRef,
  labels: labelsRouteRef,
} satisfies Record<RoutesIds, ExternalRouteRef> &
  Record<ExternalRoutesIds, ExternalRouteRef>;
